export function cleanMd(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .replace(/<imageurl>\s*https?:\/\/[^\s<]+\s*<\/imageurl>/gi, '')
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .trim();
}

export function extractImageFromCell(cellText) {
  if (!cellText) return null;

  const imageurlMatch = cellText.match(/<imageurl>\s*(https?:\/\/[^\s<]+)\s*<\/imageurl>/i);
  if (imageurlMatch) {
    return imageurlMatch[1].trim();
  }

  const mdImageMatch = cellText.match(/!\[.*?\]\((https?:\/\/[^\s)]+)\)/);
  if (mdImageMatch) {
    return mdImageMatch[1].trim();
  }

  return null;
}

export function parseTable(lines, start) {
  const headers = [];
  const rows = [];
  let i = start;

  if (lines[i]?.includes('|')) {
    const cells = lines[i].split('|').map(c => c.trim()).filter(c => c && !/^-+$/.test(c));
    if (cells.length) headers.push(...cells.map(cleanMd));
    i++;
  }

  if (lines[i]?.match(/^\|?\s*[-:]+/)) i++;

  while (i < lines.length && lines[i]?.includes('|')) {
    const line = lines[i].trim();
    if (!line || /^[-:|s]+$/.test(line)) { i++; continue; }
    const rawCells = line.split('|').map(c => c.trim()).filter(c => c);

    const processedCells = rawCells.map(cell => {
      const imageUrl = extractImageFromCell(cell);
      const text = cleanMd(cell);
      return { text, imageUrl };
    });

    if (processedCells.length) rows.push(processedCells);
    i++;
  }

  return { headers, rows, end: i };
}

export function parseList(lines, start) {
  const items = [];
  let i = start;

  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) { i++; continue; }

    const numMatch = line.match(/^(\d+)\.\s+(.+)$/);
    const bulletMatch = line.match(/^[-*]\s+(.+)$/);

    if (numMatch) {
      items.push({ number: numMatch[1], text: cleanMd(numMatch[2]) });
      i++;
    } else if (bulletMatch) {
      items.push({ bullet: true, text: cleanMd(bulletMatch[1]) });
      i++;
    } else break;
  }

  return { items, end: i };
}

export function parseContent(content) {
  content = content
    .replace(/([.!?:;])\s*(#{1,4}\s)/g, '$1\n\n$2')
    .replace(/([a-z0-9])(#{1,4}\s)/gi, '$1\n\n$2');
  const blocks = [];
  const lines = content.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) { i++; continue; }

    if (/^[-*_]{3,}$/.test(line)) {
      blocks.push({ type: 'divider' });
      i++; continue;
    }

    const hMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (hMatch) {
      blocks.push({ type: `h${hMatch[1].length}`, text: cleanMd(hMatch[2]) });
      i++; continue;
    }

    if (line.startsWith('|')) {
      const { headers, rows, end } = parseTable(lines, i);
      if (rows.length || headers.length) {
        blocks.push({ type: 'table', headers, rows });
      }
      i = end; continue;
    }

    if (/^(\d+\.|-|\*)\s/.test(line)) {
      const { items, end } = parseList(lines, i);
      blocks.push({ type: 'list', items });
      i = end; continue;
    }

    let para = line;
    i++;
    while (i < lines.length && lines[i].trim() && !/^(#{1,4}(\s|$)|[-*_]{3,}$|\||\d+\.|-\s|\*\s)/.test(lines[i].trim())) {
      if (lines[i].includes('##')) {
        break;
      }
      para += ' ' + lines[i].trim();
      i++;
    }
    blocks.push({ type: 'paragraph', text: cleanMd(para) });
  }

  return blocks;
}

export function extractTablesFromContent(content) {
  return parseContent(content || '').filter(block => block.type === 'table');
}

export function parseChartConfig(chartObj) {
  if (chartObj.config && typeof chartObj.config === 'object') {
    return { config: chartObj.config, title: chartObj.title };
  }
  if (chartObj.data && typeof chartObj.data === 'string') {
    try {
      const parsed = JSON.parse(chartObj.data);
      if (parsed.config) return parsed;
    } catch (e) {
      console.warn('Failed to parse chart.data JSON string:', e);
    }
  }
  if (chartObj.data && typeof chartObj.data === 'object' && chartObj.data.config) {
    return chartObj.data;
  }
  return null;
}

export function extractChartData(chart) {
  if (chart.config?.data) {
    return chart.config.data;
  }

  if (!chart.html) return null;

  try {
    const jsonMatch = chart.html.match(/data:\s*JSON\.parse\(['"](.*?)['"]\)/s);
    if (jsonMatch) {
      const jsonStr = jsonMatch[1]
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');
      return JSON.parse(jsonStr);
    }

    const dataMatch = chart.html.match(/data:\s*(\{[\s\S]*?\}),?\s*options:/);
    if (dataMatch) {
      const dataStr = dataMatch[1]
        .replace(/'/g, '"')
        .replace(/(\w+):/g, '"$1":');
      return JSON.parse(dataStr);
    }
  } catch (e) {
    console.error('Chart data extraction failed:', e);
  }

  return null;
}
